'use client';

import { useEffect, useRef, useState, type UIEvent } from 'react';
import Link from 'next/link';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import type { MarketStats } from '@/lib/api';
import { formatPrice, pluralize, slugify } from '@/lib/utils';
import { terminalGutter } from '@/lib/tableGutter';

type SortKey =
  | 'city'
  | 'active_total'
  | 'median_price'
  | 'median_ppsf'
  | 'median_sqft'
  | 'median_dom'
  | 'price_cut_share'
  | 'new_share';

type Sort = { key: SortKey; dir: 'asc' | 'desc' };

/** Labels for the mobile "Rank by" select (headers are the desktop affordance). */
const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'active_total', label: 'Active listings' },
  { key: 'median_price', label: 'Median price' },
  { key: 'median_ppsf', label: '$/sqft' },
  { key: 'median_sqft', label: 'Median sqft' },
  { key: 'median_dom', label: 'Days since listed' },
  { key: 'price_cut_share', label: 'Price cuts' },
  { key: 'new_share', label: 'New this week' },
  { key: 'city', label: 'City name' },
];

/** Honest region-level rollup (see /markets page for the reasoning). */
function regionRollup(rows: MarketStats[]) {
  const totalActive = rows.reduce((sum, r) => sum + r.active_total, 0);
  const medians = rows.map((r) => r.median_price).filter((p) => p > 0);
  return {
    cityCount: rows.length,
    totalActive,
    low: medians.length ? Math.min(...medians) : 0,
    high: medians.length ? Math.max(...medians) : 0,
  };
}

function compare(a: MarketStats, b: MarketStats, key: SortKey): number {
  if (key === 'city') return a.city.localeCompare(b.city);
  return (a[key] as number) - (b[key] as number);
}

function sortRows(rows: MarketStats[], sort: Sort): MarketStats[] {
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => dir * compare(a, b, sort.key));
}

/** Header cell: a sort toggle for sortable columns, plain text otherwise. */
function Th({
  label,
  sortKey,
  sort,
  onSort,
  className = '',
}: {
  label: string;
  sortKey?: SortKey;
  sort: Sort;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const base = 'py-3 font-semibold';
  if (!sortKey) {
    return <th className={`${base} ${className}`}>{label}</th>;
  }
  const active = sort.key === sortKey;
  return (
    <th
      className={`${base} ${className}`}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-ink"
      >
        {label}
        {active ? (
          sort.dir === 'asc' ? (
            <ChevronUp className="h-3 w-3 text-emerald-600" aria-hidden />
          ) : (
            <ChevronDown className="h-3 w-3 text-emerald-600" aria-hidden />
          )
        ) : (
          <ChevronDown className="h-3 w-3 text-ink-faint/50" aria-hidden />
        )}
      </button>
    </th>
  );
}

/**
 * The city column has to stay put while the numbers scroll.
 *
 * The table is 820 px wide inside a 343 px phone viewport, so reading `$/sqft`,
 * `Days listed`, `Cuts` or `New/wk` pushes the city name **461 px off the left
 * edge** — measured on the deployed site — and leaves a row of four numbers with
 * no subject. Pinning it costs at most 115 px (the longest name we carry is
 * "San Francisco"), which leaves ~228 px for the numbers.
 *
 * `bg-canvas`, not a tint: an unpainted sticky cell lets the scrolling numbers
 * show through it. And the divider appears **only once the table is scrolled**,
 * because a hard vertical edge on an unscrolled table reads as a rendering
 * fault rather than a pinned column — the W11 R108 defect, where an opaque block
 * with a hard edge cut a filter pill in half and looked broken.
 *
 * `whitespace-nowrap` because 8 of the 40 names wrapped at 390 — "San /
 * Francisco", "Mountain / View", "Redwood / City", "Scotts / Valley" — which is
 * the R128 defect (a place name split mid-name reads as a rendering fault) and
 * left those eight rows **77 px tall against their neighbours' 53**. The width
 * it takes comes off `Middle 50%`, which is why the whole table is `nowrap` too
 * (R147): nothing in it is prose, and letting `$850K–$1.4M` break instead just
 * moved the tall rows from 8 to all 40.
 *
 * The divider is a 1 px hairline plus a soft falloff. R143 shipped only the
 * falloff (`-8px` spread against a 10 px blur) and it was **invisible in the
 * deployed screenshot** — a pin nobody can see is a pin that reads as a bug in
 * the scroll.
 */
const CITY_CELL = 'sticky left-0 z-10 whitespace-nowrap bg-canvas';
const CITY_DIVIDER =
  'shadow-[1px_0_0_0_rgb(32_29_21_/_18%),7px_0_9px_-5px_rgb(32_29_21_/_14%)]';

/**
 * Every column but the first is a number, and they are set flush right.
 *
 * Two reasons, and the second is a bug fix. Right-aligned figures put their
 * digits in one vertical stack, which is what makes a ranking table readable at
 * a glance. And it keeps the digits **out of the pinned column's way**: at
 * maximum horizontal scroll one column always straddles the pin, and
 * left-aligned it lost its leading digits to it — measured on the deployed site
 * at 390, the pin's right edge at x 135 against `Days listed` ink starting at
 * x 108, so 94/124/112 read as "4"/"24"/"2" with nothing left to scroll (R148
 * flagged it as the wave's last residual).
 *
 * R148 proposed trailing padding equal to the pin's width. Measuring it first
 * showed that is the wrong sign: trailing space *raises* max scroll, dragging
 * the straddling column further under the pin, and the value that would land a
 * column boundary on the pin's edge is 84–106 px per table — a blank third of a
 * phone viewport, needing a layout effect to compute. Flush-right costs nothing
 * and fixes every scroll position rather than only the last one: the ink of a
 * three-digit number now sits ~40 px clear of the pin at max scroll instead of
 * 27 px under it.
 *
 * **R160: that last paragraph was measured, correct, and tuned to the data.**
 * "The ink of a three-digit number" was the point — when the drain halved the
 * corpus the median days-since-listed fell 110 → 49, `Days listed` lost a digit,
 * every column after it shifted left, and `Median sqft` came to rest across the
 * pin instead (`"1,502"` ink 68–108 against a pin edge at 98). Flush-right stays
 * — it is right on its own merits — but what keeps the *terminal* position clean
 * is now `lib/tableGutter`, which measures the geometry instead of assuming it.
 */
const NUM_CELL = 'py-3.5 text-right tabular-nums';
/** Every numeric cell but the last carries the column gutter. */
const NUM_GAP = `${NUM_CELL} pr-4`;
/** The same two rules on the header, so a column and its heading line up. */
const NUM_HEAD = 'pr-4 text-right';

/**
 * Region-grouped market tables with a shared, sortable column state — the
 * "decide where to buy" comparison. Server-rendered on first paint (data is in
 * the HTML for SEO), then hydrated so a buyer can rank cities by any metric
 * ($/sqft, days since listed, price-cut rate…). One sort applies across every
 * region so the ranking reads consistently; region grouping + anchors persist.
 */
export default function MarketRegionTables({
  regions,
}: {
  regions: [string, MarketStats[]][];
}) {
  const [sort, setSort] = useState<Sort>({ key: 'active_total', dir: 'desc' });
  const [query, setQuery] = useState('');

  // City reads best A–Z; every numeric metric leads with the highest.
  const defaultDir = (key: SortKey): Sort['dir'] => (key === 'city' ? 'asc' : 'desc');

  function onSort(key: SortKey) {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: defaultDir(key) }
    );
  }

  const filtered = filterRegions(regions, query);
  const shown = filtered.reduce((n, [, rows]) => n + rows.length, 0);
  const cityTotal = regions.reduce((n, [, rows]) => n + rows.length, 0);
  const filtering = normalizeCity(query).length > 0;

  return (
    <>
      {/**
       * Find a city.
       *
       * The page carries 40 cities in five region tables, and the only way to
       * reach one was to already know its region — a buyer looking for Fremont
       * has to guess "East Bay" before they can scan for it. Filtering happens
       * client-side over data already in the HTML, so the server still ships all
       * 40 rows for crawlers and the first paint is unchanged.
       */}
      <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2">
        <label htmlFor="market-city" className="text-xs uppercase tracking-wider text-ink-muted">
          Find a city
        </label>
        {/* Full-width row of its own below `sm`: with the count and "Show all"
            beside it, `flex-1` squeezed the field to ~120 px at 390 — narrow
            enough to clip "south san francisco" while it was being typed. */}
        <div className="relative min-w-0 flex-1 basis-full sm:basis-auto sm:max-w-xs">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint"
            aria-hidden
          />
          <input
            id="market-city"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Fremont, Palo Alto…"
            autoComplete="off"
            className="w-full rounded-lg border border-hairline-strong bg-surface py-2 pl-9 pr-3 text-sm text-ink placeholder:text-ink-faint"
          />
        </div>
        {filtering && (
          <>
            <p aria-live="polite" className="text-sm tabular-nums text-ink-muted">
              {shown === 0
                ? 'No match'
                : `${pluralize(shown, 'city', 'cities')} of ${cityTotal}`}
            </p>
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-sm font-medium text-emerald-700 underline-offset-4 transition-colors hover:text-emerald-600 hover:underline"
            >
              Show all
            </button>
          </>
        )}
      </div>

      {/* Region rollups — jump-nav + honest aggregates (total inventory, city
          count, span of city medians). Rendered from the *filtered* regions so
          it can never offer an anchor to a section that isn't on the page. */}
      {filtered.length > 1 && (
        <nav aria-label="Jump to a region" className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {filtered.map(([region, rows]) => {
            const r = regionRollup(rows);
            return (
              <a
                key={region}
                href={`#${slugify(region)}`}
                className="glass group rounded-card p-4 transition-colors hover:border-emerald-500/30 hover:bg-emerald-500/8"
              >
                <p className="font-medium text-ink transition-colors group-hover:text-emerald-700">
                  {region}
                </p>
                <p className="mt-1 text-xs tabular-nums text-ink-muted">
                  {pluralize(r.cityCount, 'city', 'cities')} · {r.totalActive.toLocaleString()} homes
                </p>
                <p className="mt-0.5 text-xs tabular-nums text-ink-faint">
                  {r.low === r.high
                    ? `median ${formatPrice(r.low)}`
                    : `medians ${formatPrice(r.low)}–${formatPrice(r.high)}`}
                </p>
              </a>
            );
          })}
        </nav>
      )}

      {/* Both sort affordances are gated on there being something to sort — a
          "tap any column heading" line and a Rank-by control above zero tables
          is the R116 defect: a page offering controls for content it isn't
          showing. */}
      {shown > 0 && (
        <>
          <p className="mt-8 text-sm text-ink-faint">
            Tap any column heading to rank cities — lowest $/sqft, freshest inventory, most price
            cuts.
          </p>

          {/* Mobile sort control — on desktop the column headers do this job, but
              they can sit off-screen in the horizontal scroll on a phone. */}
          <div className="mt-6 flex items-center gap-2 sm:hidden">
            <label htmlFor="market-sort" className="text-xs uppercase tracking-wider text-ink-muted">
              Rank by
            </label>
            <select
              id="market-sort"
              value={sort.key}
              onChange={(e) => {
                const key = e.target.value as SortKey;
                setSort({ key, dir: defaultDir(key) });
              }}
              className="flex-1 rounded-lg border border-hairline-strong bg-surface px-3 py-2 text-sm text-ink"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() =>
                setSort((prev) => ({ ...prev, dir: prev.dir === 'asc' ? 'desc' : 'asc' }))
              }
              className="inline-flex items-center gap-1 rounded-lg border border-hairline-strong px-3 py-2 text-sm text-ink-soft transition-colors hover:text-ink"
              aria-label={`Sort ${sort.dir === 'asc' ? 'ascending' : 'descending'} — tap to reverse`}
            >
              {sort.dir === 'asc' ? (
                <ChevronUp className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronDown className="h-4 w-4" aria-hidden />
              )}
              {sort.dir === 'asc' ? 'Asc' : 'Desc'}
            </button>
          </div>
        </>
      )}

      {filtered.map(([region, rows]) => (
        <RegionTable key={region} region={region} rows={rows} sort={sort} onSort={onSort} />
      ))}

      {/**
       * Nothing matched. Says which universe was searched, because "no match"
       * over a page titled "Bay Area market stats" reads as "we don't cover
       * Berkeley" when the truth is narrower: this page publishes medians for
       * the cities the stats endpoint returns, and search covers more than that.
       */}
      {filtering && shown === 0 && (
        <div className="mt-10 max-w-xl">
          <p className="text-ink">
            No city on this page matches{' '}
            <span className="font-medium">“{query.trim()}”</span>.
          </p>
          <p className="mt-2 text-sm text-ink-muted">
            We publish medians for the {cityTotal} Bay Area cities with enough active inventory to
            compute them. Our search covers more than that — try it by name, ZIP or address.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setQuery('')}
              className="rounded-lg border border-hairline-strong px-4 py-2 text-sm font-medium text-ink-soft transition-colors hover:text-ink"
            >
              Show all {cityTotal} cities
            </button>
            <Link
              href={`/homes?q=${encodeURIComponent(query.trim())}`}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-canvas transition-colors hover:bg-emerald-700"
            >
              Search homes for “{query.trim()}”
            </Link>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Case- and punctuation-insensitive city key.
 *
 * The API returns cities upper-cased ("SAN JOSE") and inconsistently spaced, and
 * a buyer types "palo alto", "Palo-Alto" or "paloalto". Stripping everything but
 * letters and digits makes all of those find the same row — the same class of
 * normalisation `slugify` already does for URLs.
 */
function normalizeCity(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Regions with only the matching cities, dropping regions left empty. An empty
 * query returns the input untouched, so the unfiltered page does no work and
 * hydration matches the server exactly.
 */
export function filterRegions(
  regions: [string, MarketStats[]][],
  query: string
): [string, MarketStats[]][] {
  const q = normalizeCity(query);
  if (!q) return regions;
  return regions
    .map(([region, rows]): [string, MarketStats[]] => [
      region,
      rows.filter((r) => normalizeCity(r.city).includes(q)),
    ])
    .filter(([, rows]) => rows.length > 0);
}

/**
 * One region's table, owning its own horizontal-scroll state.
 *
 * Per-table rather than lifted, because the five tables scroll independently —
 * a shared flag would draw the pinned-column divider on tables the reader has
 * not scrolled.
 */
function RegionTable({
  region,
  rows,
  sort,
  onSort,
}: {
  region: string;
  rows: MarketStats[];
  sort: Sort;
  onSort: (key: SortKey) => void;
}) {
  const [scrolled, setScrolled] = useState(false);
  const rollup = regionRollup(rows);
  const sorted = sortRows(rows, sort);

  /**
   * The trailing gutter that keeps the last scroll position off a number.
   *
   * Measured, not chosen — see `lib/tableGutter`. Re-measured whenever the
   * container or the table changes size, which covers a rotation, a font swap,
   * the finder narrowing the rows, and a sort that brings a wider value to the
   * top. `offsetLeft` is read off the `<tr>` and its non-pinned cells, never off
   * the sticky one, whose offset moves with the scroll.
   */
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [gutter, setGutter] = useState(0);
  useEffect(() => {
    const scroller = scrollerRef.current;
    const table = scroller?.querySelector('table');
    if (!scroller || !table) return;
    const measure = () => {
      const row = table.querySelector('tbody tr') as HTMLElement | null;
      const cells = row ? (Array.from(row.children) as HTMLElement[]) : [];
      const next =
        cells.length > 1
          ? terminalGutter({
              content: table.offsetWidth,
              viewport: scroller.clientWidth,
              pinWidth: cells[0].offsetWidth,
              boundaries: cells.slice(1).map((c) => c.offsetLeft - row!.offsetLeft),
            })
          : 0;
      setGutter((prev) => (prev === next ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    observer.observe(table);
    return () => observer.disconnect();
  }, [sorted, sort]);
  // The divider is the only thing this state drives, so a boolean flip per
  // threshold crossing is all the work React has to do while a finger drags.
  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const next = e.currentTarget.scrollLeft > 4;
    setScrolled((prev) => (prev === next ? prev : next));
  };
  const cityCell = `${CITY_CELL} ${scrolled ? CITY_DIVIDER : ''}`;

  return (
    <section id={slugify(region)} className="mt-12 scroll-mt-24" aria-label={`${region} market stats`}>
      <h2 className="font-display text-2xl tracking-tight text-ink">{region}</h2>
      <p className="mt-1 text-sm tabular-nums text-ink-muted">
        {pluralize(rollup.cityCount, 'city', 'cities')} · {rollup.totalActive.toLocaleString()}{' '}
        active listings ·{' '}
        {rollup.low === rollup.high
          ? `median ${formatPrice(rollup.low)}`
          : `city medians ${formatPrice(rollup.low)}–${formatPrice(rollup.high)}`}
      </p>
      <div
        ref={scrollerRef}
        className="mt-4 overflow-x-auto"
        style={gutter ? { paddingRight: gutter } : undefined}
        onScroll={onScroll}
      >
        {/* `whitespace-nowrap` on the table, not just the pinned cell.
            R145 stopped the names wrapping and the ~25 px it took came off
            `Middle 50%`, so `$850K–$1.4M` broke in two instead — which took the
            rows from "8 of 40 are 77 px" to "all 40 are 77 px", i.e. uniform but
            uniformly wrong, and ~960 px of extra page height. Every cell here is
            a place name or a formatted number; none of it is prose that wants to
            wrap, so the table takes its natural width and scrolls, which is the
            interaction the pinned column exists to make safe. */}
        <table className="w-full min-w-[820px] whitespace-nowrap text-left">
          <thead>
            <tr className="border-b border-hairline-strong text-xs uppercase tracking-wider text-ink-muted">
              <Th label="City" sortKey="city" sort={sort} onSort={onSort} className={`${cityCell} pr-4`} />
              <Th label="Active" sortKey="active_total" sort={sort} onSort={onSort} className={NUM_HEAD} />
              <Th label="Median price" sortKey="median_price" sort={sort} onSort={onSort} className={NUM_HEAD} />
              <Th label="Middle 50%" sort={sort} onSort={onSort} className={NUM_HEAD} />
              <Th label="$/sqft" sortKey="median_ppsf" sort={sort} onSort={onSort} className={NUM_HEAD} />
              <Th label="Median sqft" sortKey="median_sqft" sort={sort} onSort={onSort} className={NUM_HEAD} />
              {/* Was "DOM" — a term of art for a measurement we don't have. The
                  feed's DaysOnMarket is empty, so the number is days since each
                  home's list date. */}
              <Th label="Days listed" sortKey="median_dom" sort={sort} onSort={onSort} className={NUM_HEAD} />
              <Th label="Cuts" sortKey="price_cut_share" sort={sort} onSort={onSort} className={NUM_HEAD} />
              <Th
                label="New/wk"
                sortKey="new_share"
                sort={sort}
                onSort={onSort}
                className="text-right"
              />
            </tr>
          </thead>
          <tbody>
            {sorted.map((m) => (
              <tr key={m.city} className="border-b border-hairline">
                <td className={`py-3.5 pr-4 ${cityCell}`}>
                  <Link
                    href={`/homes/${slugify(m.city)}`}
                    className="font-medium text-ink transition-colors hover:text-emerald-700"
                  >
                    {m.city}
                  </Link>
                </td>
                <td className={`${NUM_GAP} text-ink-soft`}>{m.active_total.toLocaleString()}</td>
                <td className={`${NUM_GAP} font-medium text-ink`}>
                  {formatPrice(m.median_price)}
                </td>
                <td className={`${NUM_GAP} text-ink-soft`}>
                  {formatPrice(m.p25_price)}–{formatPrice(m.p75_price)}
                </td>
                <td className={`${NUM_GAP} text-ink-soft`}>${m.median_ppsf.toLocaleString()}</td>
                <td className={`${NUM_GAP} text-ink-soft`}>
                  {m.median_sqft > 0 ? m.median_sqft.toLocaleString() : '—'}
                </td>
                <td className={`${NUM_GAP} text-ink-soft`}>{m.median_dom}</td>
                <td className={`${NUM_GAP} text-ink-soft`}>
                  {Math.round(m.price_cut_share * 100)}%
                </td>
                <td className={`${NUM_CELL} text-ink-soft`}>
                  {m.new_share > 0 ? `${Math.round(m.new_share * 100)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
